/**
 * 아이콘 — 와이어프레임 `ICON` 사전(1030–1040행)을 그대로 옮겼다. 도형을 새로 그리지 않았다.
 *
 * 인라인 SVG 로 둔 이유: 아이콘이 10종뿐이고 전부 `currentColor` 를 따른다.
 * 스프라이트나 아이콘 패키지를 들이면 테마 반전(다크)에서 색을 따로 관리해야 한다.
 * 크기는 토큰(`--icon-*`)이다 — 숫자를 박지 않는다.
 */

/**
 * 와이어프레임 ICON 키 그대로 + 경계 상태용 둘(`alert` · `blocked`).
 *
 * 🔴 뒤의 둘은 와이어프레임에 없다 — **「색만으로 상태를 구분하지 않는다」를 지키려고** 더했다
 *    (2026-09-22). 「못 봤다」와 「0건」이 같은 회색 한 줄이라 구분이 색조차 아니라 **문장뿐**이었다.
 */
export type IconName =
  | 'pause'
  | 'play'
  | 'stop'
  | 'shield'
  | 'recover'
  | 'list'
  | 'check'
  | 'plus'
  | 'doc'
  | 'wrench'
  | 'copy'
  | 'alert'
  | 'blocked'
  | 'trash'
  | 'expand'
  | 'collapse'
  | 'help'
  | 'close'

export type IconSize = 'sm' | 'md' | 'lg' | 'xl'

/** 채움 도형 — `fill="currentColor"` 로 그린다 */
const FILLED: ReadonlySet<IconName> = new Set<IconName>(['pause', 'play', 'stop'])

const PATHS: Record<IconName, React.ReactNode> = {
  pause: (
    <>
      <rect height="16" width="4.5" x="5" y="4" />
      <rect height="16" width="4.5" x="14.5" y="4" />
    </>
  ),
  play: <path d="M7 4l12 8-12 8z" />,
  stop: <rect height="14" width="14" x="5" y="5" />,
  shield: (
    <>
      <path d="M12 2l8 3v6c0 5-3.5 9.5-8 11-4.5-1.5-8-6-8-11V5z" />
      <path d="M12 8v5" strokeLinecap="round" />
      <circle cx="12" cy="16.5" fill="currentColor" r="1.3" stroke="none" />
    </>
  ),
  recover: (
    <>
      <path d="M4 12a8 8 0 1 1 2.3 5.7" strokeLinecap="round" />
      <path d="M4 18v-5h5" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  list: <path d="M5 7h14M5 12h14M5 17h9" strokeLinecap="round" />,
  check: <path d="M5 12.5l4.5 4.5L19 7" strokeLinecap="round" strokeLinejoin="round" />,
  plus: <path d="M12 5v14M5 12h14" strokeLinecap="round" />,
  doc: (
    <>
      <path d="M6 3h8l4 4v14H6z" strokeLinejoin="round" />
      <path d="M9 12h6M9 16h6" strokeLinecap="round" />
    </>
  ),
  wrench: (
    <path d="M14.5 6.5a4 4 0 0 0 5 5L9 22l-3-3L16.5 8.5a4 4 0 0 1-2-2z" strokeLinejoin="round" />
  ),
  /* 복사 — 겹친 두 장. `doc`(한 장 + 접힌 귀)와 도형이 겹치지 않게 「겹침」으로 갈랐다 */
  copy: (
    <>
      <rect height="13" rx="1.5" width="11" x="9" y="8" />
      <path d="M6 16H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" strokeLinecap="round" />
    </>
  ),
  /* 실패 — 삼각형 느낌표. `shield`(안전)와 도형이 겹치지 않게 삼각형으로 갈랐다 */
  alert: (
    <>
      <path d="M12 3.5 22 20.5H2z" strokeLinejoin="round" />
      <path d="M12 10v4.5" strokeLinecap="round" />
      <circle cx="12" cy="17.6" fill="currentColor" r="1.2" stroke="none" />
    </>
  ),
  /* 막힘(권한 없음 · 잠김) — 원에 사선. 실패가 아니라 «지금 확정된 사실»이라 도형이 차분하다 */
  blocked: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M5.6 5.6l12.8 12.8" strokeLinecap="round" />
    </>
  ),
  /* 지우기(줄 삭제) — 휴지통. 뚜껑 · 손잡이 · 통 */
  trash: (
    <>
      <path d="M4 7h16M10 4h4" strokeLinecap="round" />
      <path d="M6 7l1 13h10l1-13" strokeLinejoin="round" />
      <path d="M10 11v5M14 11v5" strokeLinecap="round" />
    </>
  ),
  /* 전체 화면 — 네 모서리가 바깥으로(모듈 앱 비전 칸 · eric 2026-10-18) */
  expand: (
    <path d="M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5" strokeLinecap="round" strokeLinejoin="round" />
  ),
  /* 전체 화면 끝내기 — 네 모서리가 안으로 */
  collapse: (
    <path d="M9 4v5H4M20 9h-5V4M15 20v-5h5M4 15h5v5" strokeLinecap="round" strokeLinejoin="round" />
  ),
  /*
   * 닫기 — 엇갈린 두 선(모달 머리 닫기 단추 · eric 2026-10-29 「X 이거.. 좀 더 크게」). 종전 글자 ✕ 는 같은 글자 단에서 작게 그려져
   * 칸을 키워도 안 커 보였다 — 선 그림이라 칸에 비례해 선다(`overlay.css` `.a-modal__close > .a-icon`). 선 굵기는 다른 아이콘과 같은 단
   */
  close: <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />,
  /* 도움말 — 원 안의 물음표(`HelpTip` · eric 2026-10-28 「물음표 아이콘 … 누르면 말풍선」). `blocked`(원 + 사선)와 안 겹치게 글리프로 가른다 */
  help: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path
        d="M9.6 9.4a2.5 2.5 0 0 1 4.8 1c0 1.7-2.4 2.1-2.4 3.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="17" fill="currentColor" r="1.1" stroke="none" />
    </>
  ),
}

export type IconProps = {
  name: IconName
  size?: IconSize | undefined
}

/**
 * 장식용이다. 의미는 옆 라벨이 전달한다 — 그래서 `aria-hidden` 이다.
 * 라벨 없이 아이콘만 쓰는 곳은 `IconButton` 이 `aria-label` 을 따로 받는다.
 */
export function Icon({ name, size = 'md' }: IconProps) {
  const filled = FILLED.has(name)

  return (
    <svg
      aria-hidden="true"
      className={`a-icon a-icon--${size}`}
      fill={filled ? 'currentColor' : 'none'}
      focusable="false"
      stroke={filled ? 'none' : 'currentColor'}
      strokeWidth={filled ? undefined : 2.2}
      viewBox="0 0 24 24"
    >
      {PATHS[name]}
    </svg>
  )
}
